import React from 'react';
import {Box, makeStyles} from '@material-ui/core';
import {Button} from 'gatsby-theme-material-ui';
import {tutorialPageUrl, tutorialSectionAnchor} from 'helpers/links';
import {usePageContext} from 'data/page-context';

const maxWidthReadability = 800;
const useStyles = makeStyles(theme => ({
  h1: {
    ...theme.typography.h3,
    padding: 0,
    textAlign: 'left',
    color: theme.palette.primary,
    fontWeight: 600,
    margin: theme.spacing(5, 0, 6),
  },
  h2: {
    ...theme.typography.h4,
    padding: 0,
    textAlign: 'left',
    color: theme.palette.primary,
    fontWeight: 800,
    margin: theme.spacing(8, 0, 6),
  },
  hr: {
    marginTop: theme.spacing(2),
  },
  legacy: {
    '& a': {
      color: theme.palette.primary.main,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& p': {
      ...theme.typography.body1,
      fontSize: 20,
      margin: theme.spacing(1, 0, 2),
    },
    '& img': {
      display: 'block',
      maxWidth: '100%',
      height: 'auto',
      maxHeight: 600,
    },
    '& h2': {
      // match h3 styles
      ...theme.typography.h4,
      padding: 0,
      textAlign: 'left',
      color: theme.palette.secondary,
      fontWeight: 800,
      margin: theme.spacing(7, 0, 4),
    },
    '& h3': {
      // match h4 styles
      ...theme.typography.h5,
      padding: 0,
      textAlign: 'left',
      color: theme.palette.primary,
      fontWeight: 600,
      margin: theme.spacing(6, 0, 3),
    },
    '& h4': {
      // match h5 styles
      ...theme.typography.h5,
      padding: 0,
      textAlign: 'left',
      color: theme.palette.secondary,
      fontWeight: 600,
      margin: theme.spacing(6, 0, 3),
    },
    '& h5': {
      // match h6 styles
      ...theme.typography.h6,
      padding: 0,
      textAlign: 'left',
      color: theme.palette.primary,
      fontWeight: 800,
      margin: theme.spacing(6, 0, 3),
    },
    '& h6': {
      // match h6 styles
      ...theme.typography.h6,
      padding: 0,
      textAlign: 'left',
      color: theme.palette.secondary,
      fontWeight: 800,
      margin: theme.spacing(6, 0, 3),
    },
    '& embed': {
      width: '100%',
    },
    '& .quote,.info,.action,.solution,.challenge': {
      ...theme.typography.paper,
      borderLeft: '4px solid',
      padding: theme.spacing(6, 8),
      margin: theme.spacing(6, 0, 3),
      borderRadius: theme.spacing(1 / 2),
      backgroundRepeat: 'no-repeat',
      backgroundSize: '32px 24px',
      backgroundPosition: 'top 9px right 9px',
      position: 'relative',
      '& :first-child': {
        marginTop: 0,
      },
      '& :last-child': {
        marginBottom: 0,
      },
    },
    '& .quote': {
      borderLeftColor: theme.palette.legacy.actionHighlight.quote.primary,
      backgroundColor: theme.palette.legacy.actionHighlight.quote.secondary,
      '&:before': {
        color: theme.palette.legacy.actionHighlight.quote.primary,
        content: "'\\e244'", // format_quote e244
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(3),
        fontSize: 24,
        fontWeight: 400,
      },
    },
    '& .info': {
      borderLeftColor: theme.palette.legacy.actionHighlight.info.primary,
      backgroundColor: theme.palette.legacy.actionHighlight.info.secondary,
      '&:before': {
        color: theme.palette.legacy.actionHighlight.info.primary,
        content: "'\\f0dc'", // privacy_tip f0dc
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(3),
        fontSize: 24,
        fontWeight: 400,
      },
    },
    '& .action': {
      borderLeftColor: theme.palette.legacy.actionHighlight.action.primary,
      backgroundColor: theme.palette.legacy.actionHighlight.action.secondary,
      '&:before': {
        color: theme.palette.legacy.actionHighlight.action.primary,
        content: "'\\e312'", // keyboard e312
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(3),
        fontSize: 24,
        fontWeight: 400,
      },
    },
    '& .challenge': {
      borderLeftColor: theme.palette.legacy.actionHighlight.challenge.primary,
      backgroundColor: theme.palette.legacy.actionHighlight.challenge.secondary,
      '&:before': {
        color: theme.palette.legacy.actionHighlight.challenge.primary,
        content: "'\\ea23  Challenge'", // emoji_events ea23
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(3),
        fontSize: 24,
        fontWeight: 400,
      },
    },
    '& .solution': {
      borderLeftColor: theme.palette.legacy.actionHighlight.solution.primary,
      backgroundColor: theme.palette.legacy.actionHighlight.solution.secondary,
      height: theme.spacing(4),
      overflow: 'hidden',
      padding: theme.spacing(4, 8),
      transition: 'padding 0.25s ease-in',
      '&:before': {
        color: theme.palette.legacy.actionHighlight.solution.primary,
        content: "'\\e5d7  Show Solution'", // unfold_more e5d7
        fontFamily: 'Lato,Material Icons',
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(3),
        fontSize: 24,
        fontWeight: 400,
      },
      '&:hover': {
        cursor: 'pointer',
      },
      '& :nth-child(1n)': {
        display: 'none',
        opacity: 0,
        transition: 'opacity 0.2s ease-in',
      },
      '&Open': {
        height: 'auto',
        padding: theme.spacing(9, 8, 6),
        transition: 'padding 0.25s ease-out',
        '&:before': {
          content: "'\\e5d6  Hide Solution' !important", // unfold_less e5d6
        },
        '& :nth-child(1n)': {
          display: 'block',
          opacity: 1,
          transition: 'opacity 0.3s ease-out',
        },
      },
    },
    '& ul': {
      ...theme.typography.ul,
      marginLeft: theme.spacing(6),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    '& ol': {
      ...theme.typography.ol,
      marginLeft: theme.spacing(6),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    '& li': {
      ...theme.typography.li,
      fontSize: 20,
    },
    '& table': {
      fontSize: 20,
      backgroundColor: theme.palette.background.level2,
      border: `3px solid ${theme.palette.alternate.dark}`,
      borderRadius: theme.spacing(1 / 2),
    },
    '& thead': {
      backgroundColor: theme.palette.background.level1,
    },
    '& th': {
      padding: theme.spacing(2),
      border: `2px solid ${theme.palette.alternate.dark}`,
    },
    '& tbody': {},
    '& tr': {},
    '& td': {
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.alternate.dark}`,
    },
    '& code': {
      borderRadius: '4px',
      borderColor: theme.palette.background.level3,
      borderWidth: '1px',
      borderStyle: 'solid',
      padding: '2px',
      backgroundColor: theme.palette.background.level2,
      fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
    },
    '& pre': {
      fontSize: 16,
      padding: theme.spacing(2),
      margin: theme.spacing(4, 0),
      borderRadius: theme.spacing(1 / 2),
      overflowX: 'auto',
      backgroundColor: theme.palette.legacy.syntaxHighlight.background,
      backgroundImage: `linear-gradient(to right, ${theme.palette.legacy.syntaxHighlight.background}, ${theme.palette.legacy.syntaxHighlight.background}),linear-gradient(to right, ${theme.palette.legacy.syntaxHighlight.background}, ${theme.palette.legacy.syntaxHighlight.background}),linear-gradient(to right, rgba(0, 0, 0, 0.25), rgba(255, 255, 255, 0)),linear-gradient(to left, rgba(0, 0, 0, 0.25), rgba(255, 255, 255, 0))`,
      backgroundPosition: 'left center, right center, left center, right center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '20px 100%, 20px 100%, 10px 100%, 10px 100%',
      backgroundAttachment: 'local, local, scroll, scroll',
      '& span:nth-child(1n)': {
        display: 'inline !important',
      },
      '& .hll': {backgroundColor: theme.palette.legacy.syntaxHighlight.background},
      '& .c': {color: theme.palette.legacy.syntaxHighlight.comment} /* Comment */,
      '& .k': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword */,
      '& .l': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal */,
      '& .cm': {color: theme.palette.legacy.syntaxHighlight.comment} /* Comment.Multiline */,
      '& .cp': {color: theme.palette.legacy.syntaxHighlight.preproc} /* Comment.Preproc */,
      '& .c1': {color: theme.palette.legacy.syntaxHighlight.comment} /* Comment.Single */,
      '& .cs': {color: theme.palette.legacy.syntaxHighlight.comment} /* Comment.Special */,
      '& .kc': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword.Constant */,
      '& .kd': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword.Declaration */,
      '& .kn': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword.Namespace */,
      '& .kp': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword.Pseudo */,
      '& .kr': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword.Reserved */,
      '& .kt': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Keyword.Type */,
      '& .ld': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Date */,
      '& .m': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Number */,
      '& .s': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String */,
      '& .na': {color: theme.palette.legacy.syntaxHighlight.attribute} /* Name.Attribute */,
      '& .nb': {color: theme.palette.legacy.syntaxHighlight.reserved} /* Name.Builtin */,
      '& .nc': {color: theme.palette.legacy.syntaxHighlight.className} /* Name.Class */,
      '& .mb': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Number.Bin */,
      '& .mf': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Number.Float */,
      '& .mh': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Number.Hex */,
      '& .mi': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Number.Integer */,
      '& .mo': {color: theme.palette.legacy.syntaxHighlight.literal} /* Literal.Number.Oct */,
      '& .sb': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Backtick */,
      '& .sc': {color: theme.palette.legacy.syntaxHighlight.character} /* Literal.String.Char */,
      '& .sd': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Doc */,
      '& .s2': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Double */,
      '& .se': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Escape */,
      '& .sh': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Heredoc */,
      '& .si': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Interpol */,
      '& .sx': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Other */,
      '& .sr': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Regex */,
      '& .s1': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Single */,
      '& .ss': {color: theme.palette.legacy.syntaxHighlight.special} /* Literal.String.Symbol */,
      '& .bp': {color: theme.palette.legacy.syntaxHighlight.psuedo} /* Name.Builtin.Pseudo */,
      '& .il': {
        color: theme.palette.legacy.syntaxHighlight.literal,
      } /* Literal.Number.Integer.Long */,
    },
  },
  title: {
    fontWeight: 'bold',
  },
  divider: {
    margin: theme.spacing(3, 0),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(5, 0),
    },
  },
  navigationButtons: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(5),
  },
}));

const process = section => {
  const toggledClass = 'solutionOpen';
  const toggle = `((box, c)=>{if(!box.classList.contains('solution')){return;}const expanded = !box.classList.contains(c);expanded ? box.classList.add(c) : box.classList.remove(c);box.setAttribute('aria-expanded', expanded);})(arguments[0].target, '${toggledClass}');`;
  return section.replaceAll(
    '<div class="solution">',
    `<div class="solution" onclick="${toggle}" role="button" aria-label="Toggle solution box expansion" aria-expanded="false">`
  );
};

const LegacyTutorialPage = () => {
  const classes = useStyles();
  const {tutorial, page, track} = usePageContext();

  const tutorialPage = (
    <Box>
      {page.sections.nodes.map(section => {
        const sectionContent = process(section.htmlContent);
        return (
          <div key={section.id}>
            <h2 className={classes.h2} id={tutorialSectionAnchor(section)}>
              {section.title}
            </h2>
            <Box dangerouslySetInnerHTML={{__html: sectionContent}} className={classes.legacy} />
            <hr className={classes.divider} />
          </div>
        );
      })}
      <div className={classes.navigationButtons}>
        <Button
          to={
            page.previous && tutorialPageUrl({tutorial, page: page.previous, legacy: true, track})
          }
          disabled={page.previous === null}
        >
          Previous
        </Button>
        <Button
          to={page.next && tutorialPageUrl({tutorial, page: page.next, legacy: true, track})}
          disabled={page.next === null}
        >
          Next
        </Button>
      </div>
    </Box>
  );
  return (
    <Box mx="auto" maxWidth={maxWidthReadability} px={3} pb={3}>
      <h1 className={classes.h1}>{tutorial.title}</h1>
      {tutorialPage}
    </Box>
  );
};

export default LegacyTutorialPage;
